import { db } from "@/lib/firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

const BASE36_CHARS = "0123456789abcdefghijklmnopqrstuvwxyz";

/**
 * Converts a number to a base-36 string (0-9, a-z).
 * Padded to minimum 3 characters (000, 001, ... zzz, 1000, ...).
 */
export function numberToBase36(num: number): string {
  if (num < 0) throw new Error("Number must be non-negative");
  
  let result = "";
  let n = num;
  
  if (n === 0) return "000";
  
  while (n > 0) {
    result = BASE36_CHARS[n % 36] + result;
    n = Math.floor(n / 36);
  }
  
  // Pad with leading zeros to ensure at least 3 characters
  while (result.length < 3) {
    result = "0" + result;
  }
  
  return result;
}

/**
 * Converts a base-36 string back to a number.
 * Case-insensitive.
 */
export function base36ToNumber(str: string): number {
  const s = str.toLowerCase();
  let result = 0;
  
  for (let i = 0; i < s.length; i++) {
    const char = s[i];
    const val = BASE36_CHARS.indexOf(char);
    if (val === -1) throw new Error(`Invalid character '${char}' in base-36 string`);
    result = result * 36 + val;
  }
  
  return result;
}

/**
 * Generates the next sequential external ID.
 * Uses a Firestore transaction to ensure atomicity.
 * Returns the new ID string (e.g., "a7k").
 */
export async function generateNextExternalId(): Promise<string> {
  if (!db) throw new Error("Firestore is not initialized");
  
  const counterRef = doc(db, "counters", "userExternalId");
  
  try {
    const newId = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let nextValue = 0;
      
      if (!counterDoc.exists()) {
        // Initialize counter if it doesn't exist
        nextValue = 0;
        transaction.set(counterRef, { 
          currentValue: 0,
          lastUpdated: serverTimestamp()
        });
      } else {
        const currentData = counterDoc.data();
        nextValue = (currentData.currentValue || 0) + 1;
        
        transaction.update(counterRef, {
          currentValue: nextValue,
          lastUpdated: serverTimestamp()
        });
      }
      
      return numberToBase36(nextValue);
    });
    
    return newId;
  } catch (error) {
    console.error("Error generating external ID:", error);
    throw new Error("Failed to generate external ID");
  }
}

/**
 * Formats an external ID for display (adds # prefix and uppercases).
 * e.g., "a7k" -> "#A7K"
 */
export function formatExternalId(id: string): string {
  if (!id) return "";
  return `#${id.toUpperCase()}`;
}
